import { useId, type ReactElement, type ReactNode } from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cn } from "../lib/cn";
import {
  fieldKey,
  useFieldErrorsPublisher,
  useServerFieldError,
} from "./FieldErrors";

export interface FieldProps {
  label: string;
  /** doc 41 §7: helper text says what is expected, never blames the user. */
  hint?: string;
  /** Field-level error text, mapped from a 422 `errors[]` entry (doc 27 §6). */
  error?: string;
  /** The API's key for this field, when the label does not normalise to it (D239). */
  name?: string;
  required?: boolean;
  className?: string;
  children: (props: {
    id: string;
    "aria-describedby": string | undefined;
    invalid: boolean;
  }) => ReactElement;
}

/** Wires label/hint/error to a control so every form field is announced correctly. */
export function Field({
  label,
  hint,
  error: given,
  name,
  required,
  className,
  children,
}: FieldProps) {
  const id = useId();
  // [D239] The form's own message wins; otherwise the server's message for this field.
  const server = useServerFieldError(label, name);
  const error = given ?? server;
  // Only a field that can show the server's message says so: outside a provider nothing would
  // put it under the input, and the pop-up must list it instead.
  const shows = useFieldErrorsPublisher() !== null;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(" ");

  const described: string | undefined =
    describedBy.length > 0 ? describedBy : undefined;

  const rendered: ReactNode = children({
    id,
    "aria-describedby": described,
    invalid: Boolean(error),
  });

  return (
    <div
      className={cn("flex flex-col gap-1.5", className)}
      {...(shows ? { "data-field-key": fieldKey(name ?? label) } : {})}
    >
      <LabelPrimitive.Root
        htmlFor={id}
        className="text-xs font-medium text-secondary"
      >
        {label}
        {required ? (
          <span className="text-danger" aria-hidden="true">
            {" *"}
          </span>
        ) : null}
      </LabelPrimitive.Root>
      {rendered}
      {hint ? (
        <p id={hintId} className="text-xs text-tertiary">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
